import { DBSchema, openDB, unwrap } from 'idb';
import type { AIScientificSummary, ReferenceMetadata } from 'src/hooks/useDocumentSearchV2';

export interface AISearchRequestSnapshot {
  userId: string;
  tenantId: string;
  flowId: string;
  startedAt: number;
  indexName: string;
  filters: string[];
}

export interface AISearchHistoryEntry extends AISearchRequestSnapshot {
  sessionId: string;
  searchText: string;
  status: 'running' | 'success' | 'error';
  completedAt: number | null;
  durationMs: number | null;
  report: AIScientificSummary | null;
  references: ReferenceMetadata[];
  error: string | null;
}

interface HistoryDatabase extends DBSchema {
  deleted: { key: string; value: string };
  searches: {
    key: string;
    value: AISearchHistoryEntry;
    indexes: { owner: [string, string] };
  };
}

export const AI_SEARCH_HISTORY_DB = 'beacon-ai-search-history';
export const AI_SEARCH_HISTORY_UPDATED = 'beacon-ai-search-history-updated';

const getDatabase = () => openDB<HistoryDatabase>(AI_SEARCH_HISTORY_DB, 2, {
  upgrade(db, oldVersion) {
    if (oldVersion < 1) {
      const store = db.createObjectStore('searches', { keyPath: 'sessionId' });
      store.createIndex('owner', ['tenantId', 'userId']);
    }
    db.createObjectStore('deleted');
  },
});

let writes: Promise<unknown> = Promise.resolve();

// Serialize writes so a quick response cannot be overwritten by its initial running record.
export const saveAISearchHistory = (entry: AISearchHistoryEntry): Promise<void> => {
  const write = writes.catch(() => undefined).then(async () => {
    const db = await getDatabase();
    try {
      const tx = db.transaction(['searches', 'deleted'], 'readwrite');
      // A late response must not restore a search the user deleted while it was running.
      const rawTx = unwrap(tx);
      const deleted = rawTx.objectStore('deleted').get(entry.sessionId);
      deleted.onsuccess = () => {
        if (!deleted.result) rawTx.objectStore('searches').put(entry);
      };
      await tx.done;
      window.dispatchEvent(new Event(AI_SEARCH_HISTORY_UPDATED));
    } finally {
      db.close();
    }
  });
  writes = write;
  return write;
};

export const listAISearchHistory = async (tenantId: string, userId: string): Promise<AISearchHistoryEntry[]> => {
  await writes.catch(() => undefined);
  const db = await getDatabase();
  try {
    const entries = await db.getAllFromIndex('searches', 'owner', [tenantId, userId]);
    return entries.sort((a, b) => b.startedAt - a.startedAt);
  } finally {
    db.close();
  }
};

export const buildAISearchHistoryEntry = (
  request: AISearchRequestSnapshot,
  sessionId: string,
  searchText: string,
  status: AISearchHistoryEntry['status'],
  report: AIScientificSummary | null = null,
  references: ReferenceMetadata[] = [],
  error: string | null = null,
): AISearchHistoryEntry => {
  const completedAt = status === 'running' ? null : Date.now();
  return {
    ...request,
    sessionId,
    searchText,
    status,
    report,
    references,
    error,
    completedAt,
    durationMs: completedAt === null ? null : Math.max(0, completedAt - request.startedAt),
  };
};

// Recording must never prevent a search or discard a usable answer when storage is unavailable.
export const recordAISearchHistory = (entry: AISearchHistoryEntry): void => {
  saveAISearchHistory(entry).catch(() => undefined);
};

export const deleteAISearchHistory = (tenantId: string, userId: string, sessionId: string): Promise<void> => {
  const write = writes.catch(() => undefined).then(async () => {
    const db = await getDatabase();
    try {
      const tx = db.transaction(['searches', 'deleted'], 'readwrite');
      const rawTx = unwrap(tx);
      const saved = rawTx.objectStore('searches').get(sessionId);
      saved.onsuccess = () => {
        const entry: AISearchHistoryEntry | undefined = saved.result;
        if (entry?.tenantId === tenantId && entry.userId === userId) {
          rawTx.objectStore('searches').delete(sessionId);
          rawTx.objectStore('deleted').put(sessionId, sessionId);
        }
      };
      await tx.done;
      window.dispatchEvent(new Event(AI_SEARCH_HISTORY_UPDATED));
    } finally {
      db.close();
    }
  });
  writes = write;
  return write;
};
